/**
 * Workforce Mentoring Programs Page
 *
 * Manages mentoring programs with cohorts including:
 * - Program definitions (e.g., Centene Pre-Med Mentoring)
 * - Cohort management by student year
 * - Session scheduling with Zoom integration
 * - Participant tracking and engagement
 */

import React, { useState, useEffect } from 'react';
import {
  GraduationCap,
  Plus,
  Search,
  Users,
  Calendar,
  Video,
  ChevronRight,
  X,
  Edit2,
  Clock,
  MapPin,
  UserPlus,
  PlayCircle,
  CheckCircle,
  AlertCircle,
  ExternalLink,
  Settings,
} from 'lucide-react';
import { getWeaveinApiUrl } from '@/src/config';
import { PageHeader, PageContainer } from '@/components/weavein';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { authFetch } from '@/lib/authFetch';

// Helper to get auth token from admin dashboard localStorage
const getAuthToken = (): string | null => {
  return localStorage.getItem('auth_token');
};

interface MentoringProgram {
  id: number;
  name: string;
  description: string;
  program_type: string;
  target_audience: string;
  start_date: string;
  end_date: string | null;
  status: string;
  cohorts_count: number;
  total_participants: number;
  created_at: string;
}

interface Cohort {
  id: number;
  program_id: number;
  program_name: string;
  cohort_name: string;
  student_year: string;
  session_day: string;
  session_time: string;
  session_timezone: string;
  duration_minutes: number;
  max_participants: number;
  current_participants: number;
  sessions_count: number;
  status: string;
  start_date: string;
  end_date: string | null;
}

interface Session {
  id: number;
  cohort_id: number;
  session_number: number;
  title: string;
  session_date: string;
  session_time: string;
  duration_minutes: number;
  zoom_join_url: string | null;
  status: string;
  registered_count: number;
  attended_count: number;
}

interface Participant {
  id: number;
  cohort_id: number;
  full_name: string;
  email: string;
  phone: string | null;
  student_year: string;
  institution: string | null;
  status: string;
  attendance_rate: number;
  joined_at: string;
}

const PROGRAM_STATUS_COLORS: Record<string, string> = {
  draft: 'bg-paper-2 text-ink-700',
  active: 'bg-emerald-100 text-emerald-700',
  completed: 'bg-blue-100 text-blue-700',
  archived: 'bg-amber-100 text-amber-700',
};

const SESSION_STATUS_COLORS: Record<string, string> = {
  scheduled: 'bg-blue-100 text-blue-700',
  in_progress: 'bg-purple-100 text-purple-700',
  completed: 'bg-emerald-100 text-emerald-700',
  cancelled: 'bg-red-100 text-red-700',
};

const STUDENT_YEARS = ['Freshman', 'Sophomore', 'Junior', 'Senior', 'Graduate'];

interface NewProgramForm {
  name: string;
  description: string;
  program_type: string;
  target_audience: string;
  start_date: string;
}

const initialProgramForm: NewProgramForm = {
  name: '',
  description: '',
  program_type: 'mentoring',
  target_audience: 'Pre-med students',
  start_date: new Date().toISOString().split('T')[0],
};

interface NewCohortForm {
  program_id: string;
  cohort_name: string;
  target_year_group: string;
  max_participants: string;
  start_date: string;
  end_date: string;
  session_day_of_week: string;
  session_time: string;
  session_timezone: string;
}

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const TIMEZONES = [
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
];

// Create Cohort Modal Component
function CreateCohortModal({
  isOpen,
  onClose,
  programs,
  onCohortCreated,
}: {
  isOpen: boolean;
  onClose: () => void;
  programs: MentoringProgram[];
  onCohortCreated: () => void;
}) {
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [newCohort, setNewCohort] = useState<NewCohortForm>({
    program_id: '',
    cohort_name: '',
    target_year_group: '',
    max_participants: '25',
    start_date: new Date().toISOString().split('T')[0],
    end_date: '',
    session_day_of_week: 'Wednesday',
    session_time: '14:00',
    session_timezone: 'America/Los_Angeles',
  });

  const handleCreateCohort = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError(null);

    try {
      const response = await authFetch(getWeaveinApiUrl('/api/workforce/mentoring/cohorts'), {
        method: 'POST',
        jsonBody: {
          program_id: parseInt(newCohort.program_id),
          cohort_name: newCohort.cohort_name,
          target_year_group: newCohort.target_year_group || null,
          max_participants: parseInt(newCohort.max_participants),
          start_date: newCohort.start_date,
          end_date: newCohort.end_date || null,
          session_day_of_week: newCohort.session_day_of_week,
          session_time: newCohort.session_time,
          session_timezone: newCohort.session_timezone,
        },
      });

      if (response.ok) {
        onClose();
        setNewCohort({
          program_id: '',
          cohort_name: '',
          target_year_group: '',
          max_participants: '25',
          start_date: new Date().toISOString().split('T')[0],
          end_date: '',
          session_day_of_week: 'Wednesday',
          session_time: '14:00',
          session_timezone: 'America/Los_Angeles',
        });
        onCohortCreated();
      } else {
        const errorData = await response.json();
        // Handle Pydantic validation errors (422) which return detail as an array
        let errorMessage = 'Failed to create cohort';
        if (Array.isArray(errorData.detail)) {
          errorMessage = errorData.detail.map((e: any) => e.msg || e.message || JSON.stringify(e)).join('; ');
        } else if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (errorData.detail) {
          errorMessage = JSON.stringify(errorData.detail);
        }
        setSubmitError(errorMessage);
      }
    } catch (error) {
      console.error('Error creating cohort:', error);
      setSubmitError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/50" onClick={onClose} />
        <div className="relative bg-surface rounded-xl shadow-xl w-full max-w-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-ink">Create New Cohort</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-paper-2 rounded"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {submitError && (
            <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
              {submitError}
            </div>
          )}

          <form onSubmit={handleCreateCohort} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-ink-700 mb-1">Program *</label>
              <select
                required
                value={newCohort.program_id}
                onChange={(e) => setNewCohort({ ...newCohort, program_id: e.target.value })}
                className="w-full px-3 py-2 border border-v2border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                <option value="">Select a program...</option>
                {programs.map((program) => (
                  <option key={program.id} value={program.id}>
                    {program.name}
                  </option>
                ))}
              </select>
              {programs.length === 0 && (
                <p className="text-sm text-ink-500 mt-1">
                  No programs available. Create a program first.
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-ink-700 mb-1">Cohort Name *</label>
              <input
                type="text"
                required
                value={newCohort.cohort_name}
                onChange={(e) => setNewCohort({ ...newCohort, cohort_name: e.target.value })}
                placeholder="e.g., Spring 2025 - Freshmen"
                className="w-full px-3 py-2 border border-v2border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-ink-700 mb-1">Target Year Group</label>
                <select
                  value={newCohort.target_year_group}
                  onChange={(e) => setNewCohort({ ...newCohort, target_year_group: e.target.value })}
                  className="w-full px-3 py-2 border border-v2border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="">All years</option>
                  {STUDENT_YEARS.map((year) => (
                    <option key={year} value={year.toLowerCase()}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-ink-700 mb-1">Max Participants</label>
                <input
                  type="number"
                  value={newCohort.max_participants}
                  onChange={(e) => setNewCohort({ ...newCohort, max_participants: e.target.value })}
                  min="1"
                  max="100"
                  className="w-full px-3 py-2 border border-v2border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-ink-700 mb-1">Start Date *</label>
                <input
                  type="date"
                  required
                  value={newCohort.start_date}
                  onChange={(e) => setNewCohort({ ...newCohort, start_date: e.target.value })}
                  className="w-full px-3 py-2 border border-v2border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-ink-700 mb-1">End Date</label>
                <input
                  type="date"
                  value={newCohort.end_date}
                  onChange={(e) => setNewCohort({ ...newCohort, end_date: e.target.value })}
                  className="w-full px-3 py-2 border border-v2border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
            </div>

            <div className="bg-paper rounded-lg p-4 space-y-4">
              <h4 className="font-medium text-ink flex items-center space-x-2">
                <Clock className="w-4 h-4" />
                <span>Session Schedule</span>
              </h4>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-ink-700 mb-1">Day of Week</label>
                  <select
                    value={newCohort.session_day_of_week}
                    onChange={(e) => setNewCohort({ ...newCohort, session_day_of_week: e.target.value })}
                    className="w-full px-3 py-2 border border-v2border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  >
                    {DAYS_OF_WEEK.map((day) => (
                      <option key={day} value={day}>
                        {day}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-ink-700 mb-1">Time</label>
                  <input
                    type="time"
                    value={newCohort.session_time}
                    onChange={(e) => setNewCohort({ ...newCohort, session_time: e.target.value })}
                    className="w-full px-3 py-2 border border-v2border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-ink-700 mb-1">Timezone</label>
                <select
                  value={newCohort.session_timezone}
                  onChange={(e) => setNewCohort({ ...newCohort, session_timezone: e.target.value })}
                  className="w-full px-3 py-2 border border-v2border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  {TIMEZONES.map((tz) => (
                    <option key={tz.value} value={tz.value}>
                      {tz.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 border border-v2border rounded-lg hover:bg-paper font-medium"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting || programs.length === 0}
                className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-medium disabled:opacity-50"
              >
                {submitting ? 'Creating...' : 'Create Cohort'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default function WorkforceMentoringPage() {
  const { t } = useAppTranslation();
  const [programs, setPrograms] = useState<MentoringProgram[]>([]);
  const [cohorts, setCohorts] = useState<Cohort[]>([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<'programs' | 'cohorts'>('programs');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProgram, setSelectedProgram] = useState<MentoringProgram | null>(null);
  const [selectedCohort, setSelectedCohort] = useState<Cohort | null>(null);
  const [cohortSessions, setCohortSessions] = useState<Session[]>([]);
  const [cohortParticipants, setCohortParticipants] = useState<Participant[]>([]);
  const [showAddProgramModal, setShowAddProgramModal] = useState(false);
  const [showAddCohortModal, setShowAddCohortModal] = useState(false);
  const [newProgram, setNewProgram] = useState<NewProgramForm>(initialProgramForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const token = getAuthToken();
    if (!token) {
      console.warn('No auth token found for mentoring page');
      setLoading(false);
      return;
    }

    try {

      const [programsRes, cohortsRes] = await Promise.all([
        authFetch(getWeaveinApiUrl('/api/workforce/mentoring/programs')),
        authFetch(getWeaveinApiUrl('/api/workforce/mentoring/cohorts')),
      ]);

      if (programsRes.ok) {
        const data = await programsRes.json();
        setPrograms(data.items || []);
      }

      if (cohortsRes.ok) {
        const data = await cohortsRes.json();
        setCohorts(data.items || []);
      }
    } catch (error) {
      console.error('Error fetching mentoring data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCohortDetails = async (cohortId: number) => {
    try {
      const token = getAuthToken();
      if (!token) return;

      const [sessionsRes, participantsRes] = await Promise.all([
        authFetch(getWeaveinApiUrl(`/api/workforce/mentoring/cohorts/${cohortId}/sessions`)),
        authFetch(getWeaveinApiUrl(`/api/workforce/mentoring/cohorts/${cohortId}/participants`)),
      ]);

      if (sessionsRes.ok) {
        const data = await sessionsRes.json();
        setCohortSessions(data || []);
      }

      if (participantsRes.ok) {
        const data = await participantsRes.json();
        setCohortParticipants(data || []);
      }
    } catch (error) {
      console.error('Error fetching cohort details:', error);
    }
  };

  const handleSelectCohort = (cohort: Cohort) => {
    setSelectedCohort(cohort);
    fetchCohortDetails(cohort.id);
  };

  const handleCreateMeeting = async (sessionId: number) => {
    try {
      const token = getAuthToken();
      if (!token) return;

      const res = await authFetch(getWeaveinApiUrl(`/api/workforce/mentoring/sessions/${sessionId}/create-meeting`), {
        method: 'POST',
      });

      if (res.ok) {
        if (selectedCohort) {
          fetchCohortDetails(selectedCohort.id);
        }
      }
    } catch (error) {
      console.error('Error creating meeting:', error);
    }
  };

  const handleCreateProgram = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError(null);

    try {
      const response = await authFetch(getWeaveinApiUrl('/api/workforce/mentoring/programs'), {
        method: 'POST',
        jsonBody: newProgram,
      });

      if (response.ok) {
        setShowAddProgramModal(false);
        setNewProgram(initialProgramForm);
        fetchData();
      } else {
        const errorData = await response.json();
        // Handle Pydantic validation errors (422) which return detail as an array
        let errorMessage = 'Failed to create program';
        if (Array.isArray(errorData.detail)) {
          errorMessage = errorData.detail.map((e: any) => e.msg || e.message || JSON.stringify(e)).join('; ');
        } else if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (errorData.detail) {
          errorMessage = JSON.stringify(errorData.detail);
        }
        setSubmitError(errorMessage);
      }
    } catch (error) {
      console.error('Error creating program:', error);
      setSubmitError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const filteredPrograms = programs.filter(p =>
    p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.target_audience?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredCohorts = cohorts.filter(c =>
    c.cohort_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.program_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.student_year?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const stats = {
    totalPrograms: programs.length,
    activePrograms: programs.filter(p => p.status === 'active').length,
    totalCohorts: cohorts.length,
    totalParticipants: programs.reduce((sum, p) => sum + (p.total_participants || 0), 0),
  };

  const getUpcomingSessions = () => {
    const now = new Date();
    return cohortSessions
      .filter(s => new Date(s.session_date) >= now && s.status === 'scheduled')
      .slice(0, 3);
  };

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        eyebrow="Workforce · Mentoring"
        title={t('workforce.mentoring.title', 'Mentoring Programs')}
        description={t('workforce.mentoring.description', 'Manage mentoring programs, cohorts, and webinar sessions')}
        actions={
          <>
            <button
              onClick={() => setShowAddCohortModal(true)}
              className="inline-flex items-center gap-1.5 rounded-md border border-v2border bg-surface px-2.5 py-1.5 text-xs font-medium text-ink hover:bg-paper-2"
            >
              <Users className="w-4 h-4" />
              <span>{t('workforce.mentoring.addCohort', 'Add Cohort')}</span>
            </button>
            <button
              onClick={() => setShowAddProgramModal(true)}
              className="inline-flex items-center gap-1.5 rounded-md bg-v2accent px-3 py-1.5 text-xs font-medium text-white hover:opacity-90"
            >
              <Plus className="w-4 h-4" />
              <span>{t('workforce.mentoring.createProgram', 'Create Program')}</span>
            </button>
          </>
        }
      />
      <PageContainer>
      <div className="space-y-6">

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-surface rounded-xl p-4 border border-v2border">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <GraduationCap className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-ink-500">{t('workforce.mentoring.stats.totalPrograms', 'Total Programs')}</p>
                <p className="font-display text-2xl font-semibold text-ink">{stats.totalPrograms}</p>
              </div>
            </div>
          </div>
          <div className="bg-surface rounded-xl p-4 border border-v2border">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <PlayCircle className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-ink-500">{t('workforce.mentoring.stats.activePrograms', 'Active Programs')}</p>
                <p className="font-display text-2xl font-semibold text-ink">{stats.activePrograms}</p>
              </div>
            </div>
          </div>
          <div className="bg-surface rounded-xl p-4 border border-v2border">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Users className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-ink-500">{t('workforce.mentoring.stats.totalCohorts', 'Total Cohorts')}</p>
                <p className="font-display text-2xl font-semibold text-ink">{stats.totalCohorts}</p>
              </div>
            </div>
          </div>
          <div className="bg-surface rounded-xl p-4 border border-v2border">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <UserPlus className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-ink-500">{t('workforce.mentoring.stats.participants', 'Participants')}</p>
                <p className="font-display text-2xl font-semibold text-ink">{stats.totalParticipants}</p>
              </div>
            </div>
          </div>
        </div>

        {/* View Toggle & Search */}
        <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
          <div className="flex items-center bg-paper-2 rounded-lg p-1">
            <button
              onClick={() => setView('programs')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                view === 'programs' ? 'bg-surface shadow text-ink' : 'text-ink-500 hover:text-ink'
              }`}
            >
              {t('workforce.mentoring.viewPrograms', 'Programs')}
            </button>
            <button
              onClick={() => setView('cohorts')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                view === 'cohorts' ? 'bg-surface shadow text-ink' : 'text-ink-500 hover:text-ink'
              }`}
            >
              {t('workforce.mentoring.viewCohorts', 'Cohorts')}
            </button>
          </div>
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400" />
            <input
              type="text"
              placeholder={view === 'programs' ? t('workforce.mentoring.searchPrograms', 'Search programs...') : t('workforce.mentoring.searchCohorts', 'Search cohorts...')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-v2border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>
        </div>

        {/* Programs Grid View */}
        {view === 'programs' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {loading ? (
              <div className="col-span-full py-12 text-center text-ink-500">
                {t('workforce.mentoring.loadingPrograms', 'Loading programs...')}
              </div>
            ) : filteredPrograms.length === 0 ? (
              <div className="col-span-full py-12 text-center">
                <GraduationCap className="w-10 h-10 text-ink-400 mx-auto mb-2" />
                <p className="text-ink-500">{t('workforce.mentoring.noPrograms', 'No mentoring programs found')}</p>
                <button
                  onClick={() => setShowAddProgramModal(true)}
                  className="text-emerald-600 hover:text-emerald-700 font-medium mt-2"
                >
                  {t('workforce.mentoring.createFirstProgram', 'Create your first program')}
                </button>
              </div>
            ) : (
              filteredPrograms.map((program) => (
                <div
                  key={program.id}
                  className="bg-surface rounded-xl border border-v2border hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => setSelectedProgram(program)}
                >
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="p-2 bg-emerald-100 rounded-lg">
                        <GraduationCap className="w-6 h-6 text-emerald-600" />
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${PROGRAM_STATUS_COLORS[program.status]}`}>
                        {program.status}
                      </span>
                    </div>
                    <h3 className="font-semibold text-ink mb-1">{program.name}</h3>
                    <p className="text-sm text-ink-500 line-clamp-2 mb-4">{program.description}</p>

                    {program.target_audience && (
                      <div className="mb-4">
                        <span className="text-xs bg-paper-2 text-ink-500 px-2 py-1 rounded">
                          {program.target_audience}
                        </span>
                      </div>
                    )}

                    <div className="flex items-center space-x-4 text-sm text-ink-500">
                      <span className="flex items-center">
                        <Calendar className="w-4 h-4 mr-1" />
                        {new Date(program.start_date).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  <div className="px-6 py-4 bg-paper border-t border-v2border-muted grid grid-cols-2 gap-4">
                    <div className="text-center">
                      <p className="text-lg font-semibold text-ink">{program.cohorts_count || 0}</p>
                      <p className="text-xs text-ink-500">{t('workforce.mentoring.card.cohorts', 'Cohorts')}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-lg font-semibold text-ink">{program.total_participants || 0}</p>
                      <p className="text-xs text-ink-500">{t('workforce.mentoring.card.participants', 'Participants')}</p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Cohorts Table View */}
        {view === 'cohorts' && (
          <div className="bg-surface rounded-xl border border-v2border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-paper border-b border-v2border">
                  <tr>
                    <th className="text-left px-6 py-3 text-xs font-medium text-ink-500 uppercase">Cohort</th>
                    <th className="text-left px-6 py-3 text-xs font-medium text-ink-500 uppercase">Program</th>
                    <th className="text-left px-6 py-3 text-xs font-medium text-ink-500 uppercase">Year</th>
                    <th className="text-left px-6 py-3 text-xs font-medium text-ink-500 uppercase">Schedule</th>
                    <th className="text-left px-6 py-3 text-xs font-medium text-ink-500 uppercase">Participants</th>
                    <th className="text-left px-6 py-3 text-xs font-medium text-ink-500 uppercase">Status</th>
                    <th className="text-left px-6 py-3 text-xs font-medium text-ink-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {loading ? (
                    <tr>
                      <td colSpan={7} className="px-6 py-12 text-center text-ink-500">
                        Loading cohorts...
                      </td>
                    </tr>
                  ) : filteredCohorts.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-6 py-12 text-center">
                        <Users className="w-10 h-10 text-ink-400 mx-auto mb-2" />
                        <p className="text-ink-500">No cohorts found</p>
                      </td>
                    </tr>
                  ) : (
                    filteredCohorts.map((cohort) => (
                      <tr
                        key={cohort.id}
                        className="hover:bg-paper cursor-pointer"
                        onClick={() => handleSelectCohort(cohort)}
                      >
                        <td className="px-6 py-4">
                          <p className="font-medium text-ink">{cohort.cohort_name}</p>
                          <p className="text-sm text-ink-500">{cohort.sessions_count || 0} sessions</p>
                        </td>
                        <td className="px-6 py-4 text-sm text-ink-500">{cohort.program_name}</td>
                        <td className="px-6 py-4">
                          <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs font-medium">
                            {cohort.student_year}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-ink-500">
                          <div className="flex items-center space-x-1">
                            <Clock className="w-4 h-4 text-ink-400" />
                            <span>{cohort.session_day}s @ {cohort.session_time}</span>
                          </div>
                          <p className="text-xs text-ink-400">{cohort.session_timezone}</p>
                        </td>
                        <td className="px-6 py-4">
                          <span className="font-medium text-ink">
                            {cohort.current_participants}
                          </span>
                          <span className="text-ink-400">/{cohort.max_participants}</span>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${PROGRAM_STATUS_COLORS[cohort.status]}`}>
                            {cohort.status}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSelectCohort(cohort);
                            }}
                            className="p-1.5 text-ink-500 hover:bg-paper-2 rounded"
                          >
                            <ChevronRight className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Cohort Detail Slide-out */}
        {selectedCohort && (
          <div className="fixed inset-0 z-50 overflow-hidden">
            <div className="absolute inset-0 bg-black/50" onClick={() => setSelectedCohort(null)} />
            <div className="absolute right-0 top-0 bottom-0 w-full max-w-2xl bg-surface shadow-xl">
              <div className="h-full flex flex-col">
                {/* Header */}
                <div className="px-6 py-4 border-b border-v2border flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-ink">{selectedCohort.cohort_name}</h2>
                    <p className="text-sm text-ink-500">{selectedCohort.program_name}</p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button className="p-2 hover:bg-paper-2 rounded">
                      <Settings className="w-5 h-5 text-ink-500" />
                    </button>
                    <button
                      onClick={() => setSelectedCohort(null)}
                      className="p-2 hover:bg-paper-2 rounded"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6 space-y-6">
                  {/* Cohort Info */}
                  <div className="grid grid-cols-2 gap-4 bg-paper rounded-xl p-4">
                    <div>
                      <p className="text-sm text-ink-500">Student Year</p>
                      <p className="font-medium text-ink">{selectedCohort.student_year}</p>
                    </div>
                    <div>
                      <p className="text-sm text-ink-500">Schedule</p>
                      <p className="font-medium text-ink">
                        {selectedCohort.session_day}s @ {selectedCohort.session_time}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-ink-500">Duration</p>
                      <p className="font-medium text-ink">{selectedCohort.duration_minutes} minutes</p>
                    </div>
                    <div>
                      <p className="text-sm text-ink-500">Participants</p>
                      <p className="font-medium text-ink">
                        {selectedCohort.current_participants}/{selectedCohort.max_participants}
                      </p>
                    </div>
                  </div>

                  {/* Upcoming Sessions */}
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold text-ink">Sessions</h3>
                      <button className="text-sm text-emerald-600 hover:text-emerald-700 font-medium">
                        + Schedule Session
                      </button>
                    </div>
                    {cohortSessions.length === 0 ? (
                      <div className="text-center py-8 bg-paper rounded-xl">
                        <Video className="w-10 h-10 text-ink-400 mx-auto mb-2" />
                        <p className="text-ink-500">No sessions scheduled</p>
                        <button className="text-emerald-600 hover:text-emerald-700 font-medium mt-2 text-sm">
                          Schedule first session
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {cohortSessions.map((session) => (
                          <div
                            key={session.id}
                            className="flex items-center justify-between p-4 bg-surface border border-v2border rounded-lg"
                          >
                            <div className="flex items-center space-x-4">
                              <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600 font-semibold">
                                {session.session_number}
                              </div>
                              <div>
                                <p className="font-medium text-ink">{session.title || `Session ${session.session_number}`}</p>
                                <div className="flex items-center space-x-3 text-sm text-ink-500">
                                  <span className="flex items-center">
                                    <Calendar className="w-3 h-3 mr-1" />
                                    {new Date(session.session_date).toLocaleDateString()}
                                  </span>
                                  <span className="flex items-center">
                                    <Clock className="w-3 h-3 mr-1" />
                                    {session.session_time}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center space-x-3">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${SESSION_STATUS_COLORS[session.status]}`}>
                                {session.status}
                              </span>
                              {session.zoom_join_url ? (
                                <a
                                  href={session.zoom_join_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  className="p-2 text-blue-600 hover:bg-blue-50 rounded flex items-center space-x-1"
                                >
                                  <Video className="w-4 h-4" />
                                  <ExternalLink className="w-3 h-3" />
                                </a>
                              ) : (
                                <button
                                  onClick={() => handleCreateMeeting(session.id)}
                                  className="px-3 py-1.5 text-sm text-emerald-600 hover:bg-emerald-50 rounded border border-emerald-200"
                                >
                                  Create Meeting
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Participants */}
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold text-ink">Participants ({cohortParticipants.length})</h3>
                      <button className="text-sm text-emerald-600 hover:text-emerald-700 font-medium flex items-center space-x-1">
                        <UserPlus className="w-4 h-4" />
                        <span>Add Participant</span>
                      </button>
                    </div>
                    {cohortParticipants.length === 0 ? (
                      <div className="text-center py-8 bg-paper rounded-xl">
                        <Users className="w-10 h-10 text-ink-400 mx-auto mb-2" />
                        <p className="text-ink-500">No participants enrolled</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {cohortParticipants.map((participant) => (
                          <div
                            key={participant.id}
                            className="flex items-center justify-between p-3 bg-paper rounded-lg"
                          >
                            <div className="flex items-center space-x-3">
                              <div className="w-10 h-10 rounded-full bg-paper-2 flex items-center justify-center">
                                <span className="text-ink-500 font-medium">
                                  {participant.full_name.split(' ').map(n => n[0]).join('')}
                                </span>
                              </div>
                              <div>
                                <p className="font-medium text-ink">{participant.full_name}</p>
                                <p className="text-sm text-ink-500">{participant.email}</p>
                              </div>
                            </div>
                            <div className="flex items-center space-x-3">
                              {participant.institution && (
                                <span className="text-xs text-ink-500">{participant.institution}</span>
                              )}
                              <div className="flex items-center space-x-1">
                                {participant.attendance_rate >= 80 ? (
                                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                                ) : participant.attendance_rate >= 50 ? (
                                  <AlertCircle className="w-4 h-4 text-amber-500" />
                                ) : (
                                  <AlertCircle className="w-4 h-4 text-red-500" />
                                )}
                                <span className="text-sm font-medium text-ink-500">
                                  {participant.attendance_rate}%
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-v2border flex items-center justify-between">
                  <button className="px-4 py-2 text-ink-700 hover:bg-paper-2 rounded-lg">
                    Export Attendance
                  </button>
                  <button className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700">
                    Send Announcement
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Create Program Modal */}
        {showAddProgramModal && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <div className="fixed inset-0 bg-black/50" onClick={() => setShowAddProgramModal(false)} />
              <div className="relative bg-surface rounded-xl shadow-xl w-full max-w-md p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-ink">Create Mentoring Program</h2>
                  <button
                    onClick={() => setShowAddProgramModal(false)}
                    className="p-2 hover:bg-paper-2 rounded"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {submitError && (
                  <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
                    {submitError}
                  </div>
                )}

                <form onSubmit={handleCreateProgram} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-ink-700 mb-1">Program Name *</label>
                    <input
                      type="text"
                      required
                      value={newProgram.name}
                      onChange={(e) => setNewProgram({ ...newProgram, name: e.target.value })}
                      placeholder="e.g., Pre-Med Mentoring Program"
                      className="w-full px-3 py-2 border border-v2border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-ink-700 mb-1">Description</label>
                    <textarea
                      value={newProgram.description}
                      onChange={(e) => setNewProgram({ ...newProgram, description: e.target.value })}
                      placeholder="Describe this mentoring program..."
                      rows={3}
                      className="w-full px-3 py-2 border border-v2border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-ink-700 mb-1">Target Audience *</label>
                    <input
                      type="text"
                      required
                      value={newProgram.target_audience}
                      onChange={(e) => setNewProgram({ ...newProgram, target_audience: e.target.value })}
                      placeholder="e.g., Pre-med students, First-generation college students"
                      className="w-full px-3 py-2 border border-v2border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-ink-700 mb-1">Program Type</label>
                      <select
                        value={newProgram.program_type}
                        onChange={(e) => setNewProgram({ ...newProgram, program_type: e.target.value })}
                        className="w-full px-3 py-2 border border-v2border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      >
                        <option value="mentoring">Mentoring</option>
                        <option value="coaching">Coaching</option>
                        <option value="shadowing">Shadowing</option>
                        <option value="peer_support">Peer Support</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-ink-700 mb-1">Start Date</label>
                      <input
                        type="date"
                        value={newProgram.start_date}
                        onChange={(e) => setNewProgram({ ...newProgram, start_date: e.target.value })}
                        className="w-full px-3 py-2 border border-v2border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>
                  </div>

                  <div className="flex space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={() => setShowAddProgramModal(false)}
                      className="flex-1 px-4 py-2 border border-v2border rounded-lg hover:bg-paper font-medium"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-medium disabled:opacity-50"
                    >
                      {submitting ? 'Creating...' : 'Create Program'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Create Cohort Modal */}
        {showAddCohortModal && (
          <CreateCohortModal
            isOpen={showAddCohortModal}
            onClose={() => setShowAddCohortModal(false)}
            programs={programs}
            onCohortCreated={() => {
              fetchData();
            }}
          />
        )}
      </div>
      </PageContainer>
    </div>
  );
}
